import React, { useContext } from 'react';
import { Link, useLoaderData, useSearchParams, Outlet, redirect } from 'react-router-dom';
import { ContentFrame } from '../../components/content_frame/content_frame';
import DefaultInput, {SelectOption} from '../../components/default_input/default_input';
import { STAFF_EDIT_PATH, STAFF_ADD_PATH, DEFAULT_DELETE_PATH, STAFF_LIST_PATH, DESKTOP_PATH, LOGIN_PATH } from '../../constants/paths';
import * as apis from '../../constants/apis';
import {AuthContext} from '../../contexts';
import ModelTable, {Property} from '../../components/model_table/model_table';
import DefaultSection from '../../components/default_section/default_section';
import {getModels, getAsLocalDatetime, setUrlParameters} from '../../utils/functions';
import * as permissions from '../../constants/permissions';
import './staff_list.scss';


export async function staffListLoader({ request }) {  
  const url = new URL(request.url);
  
  const parameters = {
    is_active: url.searchParams.get("is_active")
  };

  if(parameters.is_active === 'all') {
    parameters.is_active = null;
  }
  else if (parameters.is_active === null) {
    parameters.is_active = true;
  }

  try {
    return await getModels(setUrlParameters(apis.STAFF_LIST_GET_API, parameters));
  }
  catch(errors) {
    if(errors instanceof Array) {
      for(let error of errors) {
        if(error.code === 209) {
          // Expired user session
          return redirect(LOGIN_PATH);
        }
        else if(error.code === 203) {
          // Not authorized
          return redirect(DESKTOP_PATH);
        }
      }
    }

    throw errors;    
  }
};

class StaffList extends React.Component {
  constructor(props) {
    super(props);

    let isActiveInput = this.props.searchParams.get('is_active');

    if(!isActiveInput) {
      isActiveInput = 'true';
    }
    else if(isActiveInput === 'all') {
      isActiveInput = '';
    }

    this.state = {
      isActiveInput: isActiveInput,
      isActiveFilter: isActiveInput,
      screenWidth: window.innerWidth
    };
  }

  async componentDidMount() {
    this.resizeListener = () => this.updateSize();

    window.addEventListener("resize", this.resizeListener);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.resizeListener);
  }

  updateSize() {
    this.setState({
      screenWidth: window.innerWidth
    });
  }

  getRoleText(entry) {
    return entry.role_names.map((roleName) => {
      return (
        <span
          className="staff-list__role-name"
          key={`user_rolename:${roleName}:${entry.id}`}
        >
          {roleName}
        </span>
      );
    });
  }

  getCreatedAtText(entry) {
    return (getAsLocalDatetime(entry.created_at, false).toLocaleDateString())
  }

  listHasActions() {
    return this.props.auth.permission_ids.includes(permissions.EDIT_STAFF_PERMISSION) || this.props.auth.permission_ids.includes(permissions.DELETE_STAFF_PERMISSION);
  }

  getActions(entry) {
    return (
      <div className="model-table__model-actions-container">

        {this.props.auth.permission_ids.includes(permissions.EDIT_STAFF_PERMISSION) &&
          <Link
            className="model-table__default-edit-button"
            to={`${STAFF_EDIT_PATH}${entry.id}`}
          >

              <i className="fas fa-edit"></i>

          </Link>
        }

        {(this.props.auth.permission_ids.includes(permissions.DELETE_STAFF_PERMISSION) && entry.removable) &&
          <Link
            className="model-table__default-delete-button"
            to={`${DEFAULT_DELETE_PATH.slice(1)}${entry.id}`}
          >

              <i className="far fa-trash-alt"></i>

          </Link>
        }

      </div>
    );
  }

  getIsActiveFilterText(entry) {
    let activeText;

    if(entry.is_active) {
      activeText = 'Ativo';
    }
    else {
      activeText = 'Inativo';
    }

    return activeText;
  }

  getIsActiveText(entry) {
    let activeText;
    let classModifier;

    if(entry.is_active) {
      activeText = 'Ativo';
      classModifier = 'ativo';
    }
    else {
      activeText = 'Inativo';
      classModifier = 'inativo';
    }

    return (
      <p className={`staff-list__is-active-text__${classModifier}`}>{activeText}</p>
    );
  }

  getProperties() {
    let properties = [
      Property('name', 'Nome', <i className="fas fa-id-card"></i>),
    ];

    if(this.state.screenWidth > 440) {
      properties.push(
        Property('email', 'E-mail', <i className="fas fa-envelope"></i>)
      );
    }

    if(this.state.screenWidth > 550) {
      properties.push(
        Property('role_names', 'Funções', <i className="fas fa-tag"></i>, {getDataText: this.getRoleText})
      );
    }

    if(this.state.screenWidth > 760) {
      properties.push(
        Property('created_at', 'Cadastro', <i className="fas fa-calendar-alt"></i>, {getDataText: this.getCreatedAtText, getFilterText: this.getCreatedAtText})
      );
    }

    properties = [
      ...properties,
      Property('is_active', 'Situação', <i className="fas fa-thermometer-half"></i>, {getDataText: this.getIsActiveText, getFilterText: this.getIsActiveFilterText})
    ];

    return properties;
  }

  getActiveOptions() {
    return [
      SelectOption('true', 'Ativo'),
      SelectOption('false', 'Inativo'),
      SelectOption('', 'Todos'),
    ];
  }

  mayResetFilterInputs() {
    if(this.state.isActiveInput.length > 0) {
      return true;
    }

    return false;
  }

  resetFilterInputs() {
    this.setState({
      isActiveInput: '',
    });
  }

  mayRefreshList() {
    if(this.state.isActiveInput !== this.state.isActiveFilter) {
      return true;
    }

    return false;
  }

  async refreshList() {
    if(this.mayRefreshList()) {
      
      this.props.setSearchParams(setUrlParameters("", {
        is_active: this.state.isActiveInput || 'all'
      }), {
        replace: true
      });      

      this.setState({
        isActiveFilter: this.state.isActiveInput
      });
    }
  }

  handleInputChange(event) {
    const target = event.target;
    let value = target.value;
    let name = target.name;

    const update = {[name]: value};

    this.setState(update);
  }

  render() {
    return (
      <>
        
        <Outlet />

        <ContentFrame
          headerHistory={[
            {
              path: DESKTOP_PATH,
              text: "Área de trabalho"
            },
            {
              path: STAFF_LIST_PATH,
              text: "Listar colaboradores"
            },
          ]}
          titleIcon={<i className="fas fa-clipboard-list"></i>}
          title="Listar colaboradores"
        >

          <DefaultSection
            className="staff-list"
            title="Lista de colaboradores com acesso ao sistema"
          >

            <div className="staff-list__list-actions">

              {this.props.auth.permission_ids.includes(permissions.ADD_STAFF_PERMISSION) &&
                <Link
                  className="model-table__default-button"
                  to={STAFF_ADD_PATH}
                >

                  <i className="fas fa-plus"></i> Adicionar colaborador

                </Link>
              }

            </div>

            <div className="staff-list__filters">

              <header className="staff-list__filters__header">

                <h4 className="staff-list__filters__header__text">Filtros</h4>

              </header>

              <div className="staff-list__filters__inputs">

                <div className="staff-list__filters__inputs-wrapper">

                  <DefaultInput
                    name="isActiveInput"
                    label="Situação do cadastro"
                    type="select"
                    handleInputChange={(event) => this.handleInputChange(event)}
                    value={this.state.isActiveInput || ''}
                    options={this.getActiveOptions()}
                  />

                </div>

                {this.mayResetFilterInputs() &&
                  <button
                    className="staff-list__filters__reset-button"
                    onClick={() => this.resetFilterInputs()}
                  >

                    <i className="fas fa-times"></i>

                  </button>
                }

                {this.mayRefreshList() &&
                  <button
                    className="staff-list__filters__refresh-button"
                    onClick={() => this.refreshList()}
                  >

                    <i className="fas fa-sync"></i>

                  </button>
                }

              </div>

            </div>

            <ModelTable
              properties={this.getProperties()}
              getActions={this.listHasActions() ? (entry) => this.getActions(entry) : null}
              data={this.props.users}
              initialOrderBy="name"
            >

            </ModelTable>

          </DefaultSection>

        </ContentFrame>

      </>
    );
  }
};

const WrappedStaffList = props => {
  const users = useLoaderData();
  const auth = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();

  return (
    <StaffList
      users={users}
      auth={auth}
      searchParams={searchParams}
      setSearchParams={setSearchParams}
      {...props}
    />
  );
};

export {WrappedStaffList as StaffList};
